import React, { useState, useEffect, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import { useCatchResponse } from '../../utils/useCatchResponse';

const categoryOptions = [
    { label: 'General', value: 'General' },
    { label: 'Academic', value: 'Academic' },
    { label: 'Sports', value: 'Sports' },
    { label: 'Events', value: 'Events' },
    { label: 'Holidays', value: 'Holidays' },
    { label: 'Exam', value: 'Exam' },
    { label: 'Fee Related', value: 'Fee Related' },
];

const priorityOptions = [
    { label: 'Normal', value: 'Normal' },
    { label: 'Important', value: 'Important' },
    { label: 'Urgent', value: 'Urgent' },
];

const targetTypeOptions = [
    { label: 'All', value: 'All' },
    { label: 'Specific Classes', value: 'Class' },
    { label: 'Specific Roles', value: 'Role' },
];

const roleOptions = [
    { label: 'Teacher', value: 'Teacher' },
    { label: 'Parent', value: 'Parent' },
    { label: 'Student', value: 'Student' },
];

const AddEditAnnouncement = ({ onCancel, masterData, schoolId, financialYear, userId }) => {
    const { catchResponse } = useCatchResponse();
    const fileUploadRef = useRef(null);
    const [title, setTitle] = useState('');
    const [titleErr, setTitleErr] = useState(false);
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('General');
    const [priority, setPriority] = useState('Normal');
    const [targetType, setTargetType] = useState('All');
    const [targetClasses, setTargetClasses] = useState([]);
    const [targetRoles, setTargetRoles] = useState([]);
    const [isPinned, setIsPinned] = useState(false);
    const [isPublished, setIsPublished] = useState(true);
    const [publishDate, setPublishDate] = useState(new Date());
    const [expiryDate, setExpiryDate] = useState(null);
    const [classList, setClassList] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);

    useEffect(() => {
        GetClassList();
        if (masterData?._id) {
            setTitle(masterData.Title || '');
            setDescription(masterData.Description || '');
            setCategory(masterData.Category || 'General');
            setPriority(masterData.Priority || 'Normal');
            setTargetType(masterData.TargetType || 'All');
            setTargetClasses(masterData.TargetClasses || []);
            setTargetRoles(masterData.TargetRoles || []);
            setIsPinned(masterData.IsPinned || false);
            setIsPublished(masterData.IsPublished !== false);
            setPublishDate(masterData.PublishDate ? new Date(masterData.PublishDate) : new Date());
            setExpiryDate(masterData.ExpiryDate ? new Date(masterData.ExpiryDate) : null);
            setExistingAttachments(masterData.Attachments || []);
        }
    }, []);

    const GetClassList = async () => {
        try {
            const response = await api.post(APIConstant.path.getClassList, { UserID: userId, FinancialYearId: financialYear });
            if (response.success) {
                setClassList(response.data?.filter(x => x?.Status === 'Active')?.map(x => ({
                    value: x?._id,
                    label: `${x?.ClassName}-${x?.SectionFullName}-${x?.MediumName}`,
                })) || []);
            }
        } catch (err) { console.log(err); }
    };

    const validate = () => {
        let valid = true;
        if (CommonConfig.isEmpty(title)) { setTitleErr(true); valid = false; } else { setTitleErr(false); }
        return valid;
    };

    const handleSave = async () => {
        if (!validate()) return;
        try {
            Loader.show();
            const formData = new FormData();
            if (masterData?._id) formData.append('id', masterData._id);
            formData.append('Title', title.trim());
            formData.append('Description', description);
            formData.append('Category', category);
            formData.append('Priority', priority);
            formData.append('TargetType', targetType);
            formData.append('TargetClasses', JSON.stringify(targetType === 'Class' ? targetClasses : []));
            formData.append('TargetRoles', JSON.stringify(targetType === 'Role' ? targetRoles : []));
            formData.append('IsPinned', isPinned);
            formData.append('IsPublished', isPublished);
            formData.append('PublishDate', publishDate?.toISOString() || new Date().toISOString());
            if (expiryDate) formData.append('ExpiryDate', expiryDate.toISOString());
            formData.append('FinancialYear', financialYear);
            formData.append('SchoolId', schoolId);
            formData.append('CreatedBy', userId);

            if (existingAttachments.length > 0 && attachments.length === 0) {
                formData.append('ExistingAttachments', JSON.stringify(existingAttachments));
            }
            attachments.forEach(file => formData.append('Attachments', file));

            const response = await api.post(APIConstant.path.AddAnnouncement, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (response.success) {
                Toast.success({ message: response.message });
                Loader.hide();
                onCancel();
            } else {
                Toast.error({ message: response.message });
                Loader.hide();
            }
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const handleFileSelect = (e) => {
        const picked = e.files || [];
        const merged = [...attachments];
        picked.forEach(f => {
            if (!merged.some(m => m.name === f.name && m.size === f.size)) merged.push(f);
        });
        if (merged.length > 5) Toast.error({ message: 'You can upload a maximum of 5 files' });
        setAttachments(merged.slice(0, 5));
        if (fileUploadRef.current) fileUploadRef.current.clear();
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingAttachment = (index) => {
        setExistingAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const isImage = (name) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name || '');

    return (
        <>
            <style>{`
                .announcement-file-upload-wrapper .p-button.p-fileupload-choose {
                    height: 35px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: none;
                    border: none;
                    outline: none;
                    padding: 0rem 1rem;
                    color: #ffffff;
                    font-size: 14px;
                    font-weight: 500 !important;
                    white-space: nowrap;
                    border-radius: 0.35rem;
                    background: #1c515e;
                    width: fit-content;
                }
                
                .announcement-file-upload-wrapper .p-button.p-fileupload-choose:hover {
                    background-color: #1d6c8d;
                }
            `}</style>
            <div className="p-d-flex p-flex-column p-jc-between p-mx-3 p-h-100">
                <div className="p-grid" >
                    <div className="p-col-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Title *</label>
                            <InputText className="p-w-100 p-mt-1" value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} />
                        </div>
                        {titleErr && <span style={{ color: 'red', fontSize: '12px' }}>Title is required</span>}
                    </div>

                    <div className="p-col-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Description</label>
                            <InputTextarea className="p-w-100 p-mt-1" value={description} rows={4} onChange={(e) => setDescription(e.target.value)} />
                        </div>
                    </div>

                    <div className="p-col-12 p-md-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Category</label>
                            <Dropdown className="p-w-100 p-mt-1" value={category} options={categoryOptions} onChange={(e) => setCategory(e.value)} />
                        </div>
                    </div>

                    <div className="p-col-12 p-md-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Priority</label>
                            <Dropdown className="p-w-100 p-mt-1" value={priority} options={priorityOptions} onChange={(e) => setPriority(e.value)} />
                        </div>
                    </div>

                    <div className="p-col-12 p-pt-2">
                        <div className="custom-inputs">
                            <label>Target Audience</label>
                            <Dropdown className="p-w-100 p-mt-1" value={targetType} options={targetTypeOptions} onChange={(e) => setTargetType(e.value)} />
                        </div>
                    </div>

                    {targetType === 'Class' && (
                        <div className="p-col-12 p-pt-2">
                            <div className="custom-inputs">
                                <label>Select Classes</label>
                                <MultiSelect className="p-w-100 p-mt-1" value={targetClasses} options={classList} onChange={(e) => setTargetClasses(e.value)} placeholder="Select Classes" filter display="chip" />
                            </div>
                        </div>
                    )}

                    {targetType === 'Role' && (
                        <div className="p-col-12 p-pt-2">
                            <div className="custom-inputs">
                                <label>Select Roles</label>
                                <MultiSelect className="p-w-100 p-mt-1" value={targetRoles} options={roleOptions} onChange={(e) => setTargetRoles(e.value)} placeholder="Select Roles" display="chip" />
                            </div>
                        </div>
                    )}

                    <div className="p-col-12 p-md-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Publish Date</label>
                            <Calendar className="p-w-100 p-mt-1" value={publishDate} onChange={(e) => setPublishDate(e.value)} dateFormat="dd/mm/yy" showTime showIcon />
                        </div>
                    </div>

                    <div className="p-col-12 p-md-6 p-pt-2">
                        <div className="custom-inputs">
                            <label>Expiry Date</label>
                            <Calendar className="p-w-100 p-mt-1" value={expiryDate} onChange={(e) => setExpiryDate(e.value)} dateFormat="dd/mm/yy" showIcon minDate={publishDate} />
                        </div>
                    </div>

                    <div className="p-col-6 p-pt-2">
                        <Checkbox inputId="pinned" checked={isPinned} onChange={(e) => setIsPinned(e.checked)} />
                        <label htmlFor="pinned" className="p-ml-2">Pin this announcement</label>
                    </div>

                    <div className="p-col-6 p-pt-2">
                        <Checkbox inputId="published" checked={isPublished} onChange={(e) => setIsPublished(e.checked)} />
                        <label htmlFor="published" className="p-ml-2">Publish immediately</label>
                    </div>

                    <div className="p-col-12 p-pt-3">
                        <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>Attachments</label>
                        {existingAttachments.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                                {existingAttachments.map((file, i) => {
                                    const url = `${apiBase}/Upload/Announcement/${file}?token=${CommonConfig.fileToken()}`;
                                    const name = file.replace(/^\d+-/, '');
                                    return (
                                        <div key={i} style={{ position: 'relative', width: '90px' }}>
                                            <a href={url} target="_blank" rel="noopener noreferrer">
                                                {isImage(file)
                                                    ? <img src={url} alt={name} style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e0e0e0' }} />
                                                    : <div style={{ width: '90px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: '1px solid #e0e0e0', background: '#f6f7f9' }}><i className="pi pi-file" style={{ fontSize: '30px', color: '#6c757d' }}></i></div>}
                                            </a>
                                            <Button icon="pi pi-times" className="p-button-rounded p-button-sm p-button-danger" style={{ position: 'absolute', top: '-8px', right: '-8px', width: '22px', height: '22px' }} onClick={() => removeExistingAttachment(i)} />
                                            <div style={{ fontSize: '11px', marginTop: '4px', wordBreak: 'break-all', textAlign: 'center' }}>{name}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="announcement-file-upload-wrapper">
                            <FileUpload ref={fileUploadRef} name="Attachments" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                                customUpload auto uploadHandler={handleFileSelect} mode="basic" chooseLabel="Choose Files" maxFileSize={5000000} />
                        </div>
                        {attachments.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                                {attachments.map((file, i) => (
                                    <div key={i} style={{ position: 'relative', width: '90px' }}>
                                        {isImage(file.name)
                                            ? <img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '90px', height: '90px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e0e0e0' }} />
                                            : <div style={{ width: '90px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', border: '1px solid #e0e0e0', background: '#f6f7f9' }}><i className="pi pi-file" style={{ fontSize: '30px', color: '#6c757d' }}></i></div>}
                                        <Button icon="pi pi-times" className="p-button-rounded p-button-sm p-button-danger" style={{ position: 'absolute', top: '-8px', right: '-8px', width: '22px', height: '22px' }} onClick={() => removeAttachment(i)} />
                                        <div style={{ fontSize: '11px', marginTop: '4px', wordBreak: 'break-all', textAlign: 'center' }}>{file.name}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="dialog-footer p-d-flex p-ai-center p-jc-end">
                    <Button className="p-m-0 btn-text-action-secondary p-m-1" label="Cancel" onClick={onCancel} />
                    <Button className="p-m-0 btn-text-action-primary submit-focus" label="Save" onClick={handleSave} />
                </div>
            </div>
        </>
    );
};

export default AddEditAnnouncement;
